import api from './api.service';

function unwrap<T>(res: unknown): T {
  const data = (res as { data?: unknown })?.data ?? res;
  return (data as { data?: T })?.data ?? (data as T);
}

function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const buffer = new ArrayBuffer(rawData.length);
  const outputArray = new Uint8Array(buffer);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export interface ApprovalPushPreferences {
  in_app_enabled: boolean;
  browser_push_enabled: boolean;
  mobile_push_enabled: boolean;
  email_enabled: boolean;
  notify_assigned: boolean;
  notify_overdue: boolean;
  notify_escalation: boolean;
  notify_rejection: boolean;
  notify_final: boolean;
  quiet_hours_enabled?: boolean;
  quiet_start?: string | null;
  quiet_end?: string | null;
  business_hours_only?: boolean;
  business_start?: string | null;
  business_end?: string | null;
  timezone?: string | null;
  critical_sound_enabled?: boolean;
}

export interface BrowserPushSubscriptionRow {
  id: string;
  device_label: string;
  browser_info?: string | null;
  is_active: boolean;
  failure_count: number;
  last_used_at?: string | null;
  created_at?: string;
  revoked_at?: string | null;
  endpoint_hint?: string;
}

export interface PushStatusResponse {
  subscribed: boolean;
  preferences: ApprovalPushPreferences;
  subscriptions?: BrowserPushSubscriptionRow[];
}

export interface ApprovalAccessCheck {
  allowed: boolean;
  reason?: string;
  actionUrl?: string;
  fallbackUrl: string;
}

function deriveDeviceLabel(): string {
  const ua = navigator.userAgent;
  const browser = /Edg\//i.test(ua)
    ? 'Edge'
    : /Chrome\//i.test(ua)
      ? 'Chrome'
      : /Firefox\//i.test(ua)
        ? 'Firefox'
        : /Safari\//i.test(ua)
          ? 'Safari'
          : 'Browser';
  const os = /Windows/i.test(ua)
    ? 'Windows'
    : /Mac OS|Macintosh/i.test(ua)
      ? 'macOS'
      : /Android/i.test(ua)
        ? 'Android'
        : /iPhone|iPad/i.test(ua)
          ? 'iOS'
          : /Linux/i.test(ua)
            ? 'Linux'
            : 'Device';
  return `${os} — ${browser}`;
}

export const pushNotificationService = {
  async getStatus(): Promise<PushStatusResponse> {
    const res = await api.get('/notifications/push/status');
    return unwrap<PushStatusResponse>(res);
  },

  async listSubscriptions(): Promise<BrowserPushSubscriptionRow[]> {
    const res = await api.get('/notifications/push/subscriptions');
    return unwrap<BrowserPushSubscriptionRow[]>(res) || [];
  },

  async getVapidPublicKey(): Promise<string | null> {
    const res = await api.get('/notifications/push/vapid-public-key');
    const data = unwrap<{ publicKey?: string | null }>(res);
    return data?.publicKey ?? null;
  },

  async updatePreferences(patch: Partial<ApprovalPushPreferences>): Promise<ApprovalPushPreferences> {
    const res = await api.patch('/notifications/push/approval-preferences', patch);
    return unwrap<ApprovalPushPreferences>(res);
  },

  isSupported(): boolean {
    return (
      typeof window !== 'undefined' &&
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window
    );
  },

  permissionState(): NotificationPermission | 'unsupported' {
    if (!this.isSupported()) return 'unsupported';
    return Notification.permission;
  },

  /**
   * Wait for an active service worker. `navigator.serviceWorker.ready` never resolves
   * if no SW is registered (common on plain Vite before PWA was enabled in dev).
   */
  async waitForServiceWorker(timeoutMs = 12_000): Promise<ServiceWorkerRegistration> {
    if (!('serviceWorker' in navigator)) {
      throw new Error('Service workers are not supported in this browser');
    }
    const existing = await navigator.serviceWorker.getRegistration();
    if (existing?.active) return existing;

    const ready = navigator.serviceWorker.ready;
    const timedOut = new Promise<never>((_, reject) => {
      window.setTimeout(() => {
        reject(
          new Error(
            'Service worker is not ready. Reload the page after the app finishes loading, then try Enable again.'
          )
        );
      }, timeoutMs);
    });
    return Promise.race([ready, timedOut]);
  },

  async subscribe(): Promise<boolean> {
    if (!this.isSupported()) {
      throw new Error('Browser push is not supported in this browser');
    }

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      throw new Error('Notification permission was not granted');
    }

    const publicKey = await this.getVapidPublicKey();
    if (!publicKey) {
      throw new Error('Server VAPID public key is missing — check backend VAPID_* env vars');
    }

    const registration = await this.waitForServiceWorker();
    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    }

    const json = subscription.toJSON();
    if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
      throw new Error('Browser did not return a complete push subscription');
    }

    await api.post('/notifications/push/subscribe', {
      endpoint: json.endpoint,
      p256dh: json.keys.p256dh,
      auth: json.keys.auth,
      browser_info: navigator.userAgent.slice(0, 500),
      device_label: deriveDeviceLabel(),
    });
    await this.updatePreferences({ browser_push_enabled: true });
    return true;
  },

  async unsubscribe(): Promise<void> {
    if (!this.isSupported()) return;
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return;
    const endpoint = subscription.endpoint;
    await subscription.unsubscribe();
    await api.delete('/notifications/push/subscribe', { data: { endpoint } });
    await this.updatePreferences({ browser_push_enabled: false });
  },

  async revokeSubscription(id: string): Promise<void> {
    await api.delete(`/notifications/push/subscriptions/${id}`);
  },

  async disableAll(): Promise<void> {
    if (this.isSupported()) {
      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) await subscription.unsubscribe();
      } catch {
        /* ignore local unsubscribe errors */
      }
    }
    await api.delete('/notifications/push/subscriptions');
  },

  async sendTest(): Promise<void> {
    await api.post('/notifications/push/test');
  },

  async checkApprovalAccess(params: {
    entityType?: string;
    entityId?: string;
    instanceId?: string;
  }): Promise<ApprovalAccessCheck> {
    const q = new URLSearchParams();
    if (params.entityType) q.set('entity_type', params.entityType);
    if (params.entityId) q.set('entity_id', params.entityId);
    if (params.instanceId) q.set('instance_id', params.instanceId);
    const res = await api.get(`/approval/inbox/check-access?${q.toString()}`);
    return unwrap<ApprovalAccessCheck>(res);
  },

  async recordClick(notificationId: string): Promise<void> {
    await api.patch(`/notifications/${notificationId}/click`);
  },

  async openNotificationUrl(rawUrl: string, notificationId?: string | null): Promise<string> {
    const url = rawUrl.startsWith('/') ? rawUrl : '/home/approvals';
    if (notificationId) {
      try {
        await this.recordClick(notificationId);
      } catch {
        /* non-fatal */
      }
    }
    try {
      const parsed = new URL(url, window.location.origin);
      const path = `${parsed.pathname}${parsed.search}`;
      const uuidMatch = path.match(
        /\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:\/|$|\?)/i
      );
      const access = await this.checkApprovalAccess({
        entityId: uuidMatch?.[1],
      });
      if (access.allowed && access.actionUrl) {
        return access.actionUrl;
      }
      if (!access.allowed && access.reason) {
        return access.fallbackUrl || '/home/approvals';
      }
      return path;
    } catch {
      return '/home/approvals';
    }
  },
};

export default pushNotificationService;
